import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Copy, Link as LinkIcon, MessageCircle, Plus, X } from "lucide-react";
import { crearEnlacePago, listarEnlacesPago, cancelarEnlacePago } from "@/lib/enlaces-pago.functions";

export const Route = createFileRoute("/_authenticated/gestion/enlaces-pago")({
  component: EnlacesPagoPage,
});

type Enlace = {
  id: string;
  token: string;
  concepto: string;
  importe: number;
  cliente_nombre: string | null;
  cliente_telefono: string | null;
  cliente_email: string | null;
  estado: string;
  caduca_at: string | null;
  pagado_at: string | null;
  created_at: string;
};

const fmt = (n: number) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(n || 0);

function EnlacesPagoPage() {
  const listar = useServerFn(listarEnlacesPago);
  const crear = useServerFn(crearEnlacePago);
  const cancelar = useServerFn(cancelarEnlacePago);
  const [items, setItems] = useState<Enlace[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    concepto: "",
    importe: "",
    cliente_nombre: "",
    cliente_telefono: "",
    cliente_email: "",
    caduca_dias: "7",
  });

  async function load() {
    setLoading(true);
    try {
      const data = await listar();
      setItems(((data ?? []) as unknown) as Enlace[]);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  function origin() {
    return "https://elpajardebenito.es";
  }
  function linkFor(token: string) { return `${origin()}/pago/enlace/${token}`; }

  function whatsappLink(e: Enlace) {
    const tel = (e.cliente_telefono ?? "").replace(/[^\d+]/g, "");
    const msg = `Hola${e.cliente_nombre ? " " + e.cliente_nombre : ""}, aquí tienes el enlace seguro para el pago de ${e.concepto} (${fmt(Number(e.importe))}):\n${linkFor(e.token)}\n\nEl Pajar de Benito`;
    const base = tel ? `https://wa.me/${tel.replace(/^\+/, "")}` : `https://wa.me/`;
    return `${base}?text=${encodeURIComponent(msg)}`;
  }

  async function submit() {
    const importe = Number(form.importe.replace(",", "."));
    if (!form.concepto.trim() || !importe || importe <= 0) return toast.error("Concepto e importe son obligatorios");
    try {
      await crear({
        data: {
          concepto: form.concepto.trim(),
          importe,
          cliente_nombre: form.cliente_nombre.trim() || null,
          cliente_telefono: form.cliente_telefono.trim() || null,
          cliente_email: form.cliente_email.trim() || null,
          caduca_dias: form.caduca_dias ? Number(form.caduca_dias) : null,
        },
      });
      toast.success("Enlace de pago creado");
      setOpen(false);
      setForm({ concepto: "", importe: "", cliente_nombre: "", cliente_telefono: "", cliente_email: "", caduca_dias: "7" });
      load();
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function anular(id: string) {
    if (!confirm("¿Cancelar este enlace? No se podrá pagar.")) return;
    try {
      await cancelar({ data: { id } });
      toast.success("Enlace cancelado");
      load();
    } catch (e) { toast.error((e as Error).message); }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold"><LinkIcon className="h-6 w-6" /> Enlaces de pago</h1>
          <p className="text-sm text-muted-foreground">Genera un enlace corto para cobrar por WhatsApp, SMS o email sin tener que crear una reserva completa.</p>
        </div>
        <Button onClick={() => setOpen(true)}><Plus className="mr-2 h-4 w-4" /> Nuevo enlace</Button>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : items.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground">Aún no has generado enlaces de pago.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {items.map((e) => {
            const url = linkFor(e.token);
            return (
              <Card key={e.id}>
                <CardContent className="grid gap-3 p-4 md:grid-cols-[1fr_auto] md:items-center">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-medium">{e.concepto}</span>
                      <span className="text-lg font-semibold">{fmt(Number(e.importe))}</span>
                      <Badge variant={e.estado === "pagado" ? "default" : e.estado === "pendiente" ? "secondary" : "outline"}>{e.estado}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {e.cliente_nombre ?? "Sin cliente"}{e.cliente_telefono ? ` · ${e.cliente_telefono}` : ""}
                      {e.caduca_at ? ` · Caduca ${new Date(e.caduca_at).toLocaleDateString("es-ES")}` : ""}
                      {e.pagado_at ? ` · Pagado ${new Date(e.pagado_at).toLocaleString("es-ES")}` : ""}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <Input readOnly value={url} className="h-8 font-mono text-xs" />
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 md:flex-col md:items-stretch">
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(url); toast.success("Enlace copiado"); }}>
                      <Copy className="mr-2 h-4 w-4" /> Copiar
                    </Button>
                    <Button variant="outline" size="sm" asChild>
                      <a href={whatsappLink(e)} target="_blank" rel="noreferrer" className="text-green-700">
                        <MessageCircle className="mr-2 h-4 w-4" /> WhatsApp
                      </a>
                    </Button>
                    {e.estado === "pendiente" && (
                      <Button variant="ghost" size="sm" onClick={() => anular(e.id)}>
                        <X className="mr-2 h-4 w-4 text-destructive" /> Cancelar
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Nuevo enlace de pago</DialogTitle></DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Concepto *</Label>
              <Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} placeholder="Señal reserva 12-15 marzo · Habitación Roble" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Importe (€) *</Label>
                <Input type="number" step="0.01" min="0" value={form.importe} onChange={(e) => setForm({ ...form, importe: e.target.value })} />
              </div>
              <div>
                <Label>Caduca en (días)</Label>
                <Input type="number" min="1" max="60" value={form.caduca_dias} onChange={(e) => setForm({ ...form, caduca_dias: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Nombre del cliente</Label>
              <Input value={form.cliente_nombre} onChange={(e) => setForm({ ...form, cliente_nombre: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Teléfono (para WhatsApp)</Label>
                <Input value={form.cliente_telefono} onChange={(e) => setForm({ ...form, cliente_telefono: e.target.value })} placeholder="+34 600 000 000" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.cliente_email} onChange={(e) => setForm({ ...form, cliente_email: e.target.value })} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit}>Crear enlace</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}